import React, { useEffect, useState } from 'react';
import VideoService from '../../services/KangarooServices/VideoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody, Col, Container, Row, Spinner, Badge } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { imageBaseApi } from '../../utilis/imageHelper';
import { Edit3 } from 'lucide-react';

const VideoDetails = () => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await VideoService.getdetails(id);
      setDetails(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const convertToEmbedUrl = (url) => {
    if (!url || typeof url !== "string") return "";

    // YouTube
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      let videoId = "";
      if (url.includes("v=")) {
        videoId = url.split("v=")[1]?.split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1]?.split("?")[0];
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    }

    // Vimeo
    if (url.includes("vimeo.com")) {
      const videoId = url.split("/").pop();
      return videoId ? `https://player.vimeo.com/video/${videoId}` : "";
    }

    return url;
  };

  return (
    <div className='page-content'>
      <Container fluid>
        <BreadCrumb 
          title="Details" 
          pageTitle="Video" 
          breadcrumbItems={[{ title: "Back to List", link: "/videos" }]}
        />

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row className="gy-4">
            <Col lg={12}>
              <Card className="shadow-sm border-0 overflow-hidden">
                <div className="bg-primary bg-gradient" style={{ height: '6px' }}></div>
                <CardBody className="p-4">
                  
                  {/* Header */}
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i className="bx bx-video text-primary" style={{ fontSize: '24px' }}></i>
                    </div>
                    <div className="position-absolute me-3" style={{ right: "0", zIndex: 100 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                        onClick={() => navigate(`/video-form/${id}`)}
                        style={{ fontSize: "0.875rem" }}
                      >
                        <Edit3 size={14} />
                        Update 
                      </button>
                    </div>
                    <h5 className="mb-0 fw-semibold">Video Information</h5>
                  </div>

                  <Row className="align-items-start mb-3">
                    {/* Left: Thumbnail */}
                    <Col md={5} className="mb-4 mb-md-0">
                      <div
                        className="position-relative rounded-3 overflow-hidden"
                        style={{
                          width: "100%",
                          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                          border: "1px solid rgba(0,0,0,0.05)",
                        }}
                      >
                        <div 
                          className="ratio ratio-16x9"
                          style={{
                            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                          }}
                        >
                          <img
                            src={`${imageBaseApi}${details?.thumbnailMedia?.url}`}
                            alt={details?.title}
                            className="img-fluid"
                            style={{ 
                              width: "100%", 
                              height: "100%", 
                              objectFit: "cover",
                              objectPosition: "center"
                            }}
                          />
                        </div>
                      </div>
                    </Col>

                    {/* Right: Details */}
                    <Col md={7}>
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-heading me-1"></i> Title
                            </label>
                            <p className="fw-semibold mb-0 fs-5">{details?.title}</p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-category me-1"></i> Category
                            </label>
                            <p className="fw-semibold mb-0">
                              <Badge color="primary" className="px-3 py-2">
                                {details?.category}
                              </Badge>
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-info-circle me-1"></i> Status
                            </label>
                            <p className="fw-semibold mb-0">
                              <Badge 
                                color={details?.isActive ? "success" : "secondary"} 
                                className="px-3 py-2"
                              >
                                {details?.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-world me-1"></i> Related Country Name
                            </label>
                            <p className="fw-semibold mb-0">
                              {details?.relatedCountryName || <span className="text-muted">N/A</span>}
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-building me-1"></i> Related University Name
                            </label>
                            <p className="fw-semibold mb-0">
                              {details?.relatedUniversityName || <span className="text-muted">N/A</span>}
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {/* Description */}
                  {details?.description && (
                    <div className="mb-4">
                      <label className="text-muted small mb-1 d-flex align-items-center">
                        <i className="bx bx-message-square-detail me-1"></i> Description
                      </label>
                      <div className="bg-light rounded p-3">
                        <p className="mb-0">{details?.description}</p>
                      </div>
                    </div>
                  )}

                  {/* Video */}
                  {details?.videoUrl && (
                    <div className="mt-4">
                      <label className="text-muted small mb-2 d-flex align-items-center">
                        <i className="bx bx-play-circle me-1"></i> Video
                      </label>
                      <div className="ratio ratio-16x9 rounded" style={{ overflow: 'hidden' }}>
                        <iframe
                          src={convertToEmbedUrl(details?.videoUrl)}
                          title={details?.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ border: 'none' }}
                        ></iframe>
                      </div>
                    </div>
                  )}

                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      
      </Container>
    </div>
  );
};

export default VideoDetails;
